// Copyright (c) 2025 Initiumnet Technologies. All Rights Reserved.

/*
 * "UAExploreGridLayout.js": Explore Features — choose which Kleido features are on.
 *
 *   Each card: video window (YouTube, plays in place) → name + price → description
 *              → likes / reviews → Add / Remove.
 *   Kleido Engage is on for every user (no Add / Remove).
 *   Grid: 1 card per row on phones, up to 5 per row on wide screens; equal heights.
 *   Save writes the selection plus the added / removed lists, then opens the feed.
 */

import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import kleidoLogo from "../../../../../assets/images/Kleido.png";
import "./UAExploreGridLayout.css";
import { saveSelectedFeatures } from "./utils/fetchFeatureService";

// ─── Feature definitions ────────────────────────────────────────────────────
// videoUrl: any YouTube link (watch?v=, youtu.be/, shorts/, embed/). Empty → sample image.
// locked:   always on for every user, no Add / Remove.

export const KLEIDO_FEATURES = [
  {
    code: "KN",
    title: "Kleido Engage",
    subtitle: "Your primary social connection on Kleido.",
    price: 0,
    description: "Follow, message and engage with the people who matter to you.",
    videoUrl: "",
    locked: true,
  },
  {
    code: "KE",
    title: "Kleido Gather",
    subtitle: "Events & cultural heritage across the world.",
    price: 0,
    description: "Calendar sync, basic reminders, and daily planning.",
    videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
  },
  {
    code: "KW",
    title: "Kleido Sky",
    subtitle: "Climatic predictions and disaster awareness.",
    price: 0,
    description: "Automate repetitive tasks with smart workflow triggers.",
    videoUrl: "https://www.youtube.com/embed/jNQXAC9IVRw",
  },
  {
    code: "KR",
    title: "Kleido Insight",
    subtitle: "Platform with credible reviews of products and services.",
    price: 0,
    description: "Visual reports and performance dashboards.",
    videoUrl: "https://www.youtube.com/embed/9bZkp7q19f0",
  },
  {
    code: "KG",
    title: "Kleido Community",
    subtitle: "Community driven social networking platform.",
    price: 0,
    description: "Shared workspaces and group scheduling.",
    videoUrl: "https://www.youtube.com/embed/kJQP7kiw5Fk",
  },
  {
    code: "KC",
    title: "Kleido Studio",
    subtitle: "Content creator tool chains.",
    price: 0,
    description: "Connect to Slack, Zoom, Google, and 50+ apps.",
    videoUrl: "https://www.youtube.com/embed/RgKAFK5djSk",
  },
  {
    code: "KP",
    title: "Kleido Spaces",
    subtitle: "Multi-profile account model specific to user interests.",
    price: 0,
    description: "Connect to Slack, Zoom, Google, and 50+ apps.",
    videoUrl: "https://www.youtube.com/embed/RgKAFK5djSk",
  },
  {
    code: "KA",
    title: "Kleido Advanced",
    subtitle: "More secured and advanced features.",
    price: 0,
    description: "Intelligent scheduling suggestions powered by AI.",
    videoUrl: "https://www.youtube.com/embed/JGwWNGJdvx8",
  },
];

const LOCKED_CODES = KLEIDO_FEATURES.filter((f) => f.locked).map((f) => f.code);
const SEED_LIKES = { KN: 40, KE: 12, KW: 8, KR: 15, KG: 22, KC: 31, KP: 6, KA: 9 };

const STORAGE = {
  selected: "kleido-selected-features",
  added: "kleido-add-features",
  removed: "kleido-remove-features",
  likes: (code) => `kleido-likes-${code}`,
  liked: (code) => `kleido-liked-${code}`,
  reviews: (code) => `kleido-reviews-${code}`,
};

// ─── localStorage helpers ────────────────────────────────────────────────────

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : JSON.parse(raw) ?? fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage full or blocked */
  }
}

/** Saved selection, always including the locked (default) features. */
function loadSelected() {
  const saved = readJson(STORAGE.selected, []);
  const known = Array.isArray(saved) ? saved.filter((c) => KLEIDO_FEATURES.some((f) => f.code === c)) : [];
  return [...new Set([...LOCKED_CODES, ...known])];
}

const loadLikeCount = (code) => {
  const n = Number.parseInt(readJson(STORAGE.likes(code), SEED_LIKES[code] ?? 0), 10);
  return Number.isFinite(n) && n >= 0 ? n : SEED_LIKES[code] ?? 0;
};
const loadReviews = (code) => {
  const r = readJson(STORAGE.reviews(code), []);
  return Array.isArray(r) ? r.filter((x) => typeof x === "string") : [];
};

// ─── YouTube helpers ─────────────────────────────────────────────────────────

/** 11-character video id from any YouTube link (watch, youtu.be, shorts, embed); "" if none. */
export function youTubeId(url) {
  const value = String(url || "").trim();
  if (!value) return "";
  if (/^[\w-]{11}$/.test(value)) return value;
  try {
    const u = new URL(value);
    const host = u.hostname.replace(/^www\.|^m\./, "");
    if (host === "youtu.be") return (u.pathname.split("/")[1] || "").slice(0, 11);
    if (host === "youtube.com" || host === "youtube-nocookie.com") {
      const v = u.searchParams.get("v");
      if (v) return v.slice(0, 11);
      const m = u.pathname.match(/^\/(?:embed|shorts|live|v)\/([\w-]{11})/);
      if (m) return m[1];
    }
  } catch {
    /* not a URL */
  }
  return "";
}

const thumbnailUrl = (id) => `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
const embedUrl = (id) => `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&modestbranding=1&playsinline=1`;

// ─── Video window ────────────────────────────────────────────────────────────

function PlayIcon() {
  return (
    <svg viewBox="0 0 68 48" width="56" height="40" aria-hidden="true">
      <path d="M66.5 7.7a8.5 8.5 0 0 0-6-6C55.2.3 34 .3 34 .3s-21.2 0-26.5 1.4a8.5 8.5 0 0 0-6 6C.1 13 .1 24 .1 24s0 11 1.4 16.3a8.5 8.5 0 0 0 6 6C12.8 47.7 34 47.7 34 47.7s21.2 0 26.5-1.4a8.5 8.5 0 0 0 6-6C67.9 35 67.9 24 67.9 24s0-11-1.4-16.3z" fill="rgba(0,0,0,0.65)" />
      <path d="M27 34.3 45 24 27 13.7z" fill="#fff" />
    </svg>
  );
}

/** Thumbnail + play button; the YouTube player loads only when clicked. */
function VideoWindow({ title, videoUrl }) {
  const id = youTubeId(videoUrl);
  const [playing, setPlaying] = React.useState(false);
  const [thumbFailed, setThumbFailed] = React.useState(false);

  if (id && playing) {
    return (
      <div className="ex_video">
        <iframe
          src={embedUrl(id)}
          title={`${title} — video`}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
      </div>
    );
  }

  return (
    <div className="ex_video">
      {id && !thumbFailed ? (
        <img src={thumbnailUrl(id)} alt="" loading="lazy" onError={() => setThumbFailed(true)} />
      ) : (
        <div className="ex_video-sample" aria-hidden="true" />
      )}
      {id ? (
        <button type="button" className="ex_play" onClick={() => setPlaying(true)} aria-label={`Play video: ${title}`}>
          <PlayIcon />
        </button>
      ) : (
        <div className="ex_play ex_play--soon" aria-hidden="true">
          <PlayIcon />
          <span className="ex_soon">Video coming soon</span>
        </div>
      )}
    </div>
  );
}

// ─── Reviews dialog ──────────────────────────────────────────────────────────

function ReviewsDialog({ feature, reviews, onSubmit, onReset, onClose }) {
  const [text, setText] = React.useState("");
  const [thanks, setThanks] = React.useState(false);
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    inputRef.current?.focus();
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  React.useEffect(() => {
    if (!thanks) return undefined;
    const t = setTimeout(() => setThanks(false), 2000);
    return () => clearTimeout(t);
  }, [thanks]);

  const submit = () => {
    const value = text.trim();
    if (!value) return;
    onSubmit(value);
    setText("");
    setThanks(true);
  };

  return (
    <div className="ex_modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="ex_modal" role="dialog" aria-modal="true" aria-labelledby="ex-reviews-title">
        <button type="button" className="ex_modal-close" onClick={onClose} aria-label="Close">
          ×
        </button>
        <h2 id="ex-reviews-title" className="ex_modal-title">
          Feedback for {feature.title}
        </h2>

        <textarea
          ref={inputRef}
          className="ex_modal-textarea"
          maxLength={500}
          placeholder="Share your thoughts..."
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <div className="ex_modal-row">
          <button type="button" className="ex_btn ex_btn--add ex_btn--small" onClick={submit} disabled={!text.trim()}>
            Submit
          </button>
          <span className="ex_modal-count">{text.length}/500</span>
          {thanks && (
            <span className="ex_modal-thanks" role="status">
              Thanks for your feedback!
            </span>
          )}
        </div>

        <div className="ex_modal-list">
          {reviews.length === 0 ? (
            <p className="ex_modal-empty">No reviews yet.</p>
          ) : (
            reviews.slice(0, 5).map((r, i) => (
              <p key={`${i}-${r.slice(0, 12)}`} className="ex_modal-item">
                {r}
              </p>
            ))
          )}
        </div>

        <button type="button" className="ex_link-btn" onClick={onReset}>
          Reset likes & reviews for this feature
        </button>
      </div>
    </div>
  );
}

// ─── Feature card ────────────────────────────────────────────────────────────

function FeatureCard({ feature, selected, likes, liked, reviewCount, onToggle, onLike, onOpenReviews }) {
  return (
    <article className={`ex_card${selected ? " ex_card--selected" : ""}${feature.locked ? " ex_card--locked" : ""}`}>
      <VideoWindow title={feature.title} videoUrl={feature.videoUrl} />

      <div className="ex_body">
        <div className="ex_card-top">
          <span className="ex_code">{feature.code}</span>
          {feature.locked ? (
            <span className="ex_badge ex_badge--default">Default</span>
          ) : (
            selected && <span className="ex_badge">Active</span>
          )}
        </div>

        <div className="ex_name-row">
          <h3 className="ex_name">{feature.title}</h3>
          <p className="ex_price">
            ${feature.price.toFixed(2)}
            <span>/mo</span>
          </p>
        </div>
        <p className="ex_subtitle">{feature.subtitle}</p>
        <p className="ex_desc">{feature.description}</p>

        <div className="ex_meta">
          <button
            type="button"
            className={`ex_meta-btn${liked ? " ex_meta-btn--on" : ""}`}
            onClick={onLike}
            aria-pressed={liked}
            aria-label={`${liked ? "Unlike" : "Like"} ${feature.title}`}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill={liked ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z" />
              <path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
            </svg>
            {likes}
          </button>
          <button type="button" className="ex_meta-btn" onClick={onOpenReviews} aria-label={`Reviews for ${feature.title}`}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            {reviewCount} review{reviewCount === 1 ? "" : "s"}
          </button>
        </div>

        {feature.locked ? (
          <div className="ex_included" role="note">
            <span aria-hidden="true">✓</span> Included for everyone
          </div>
        ) : (
          <button
            type="button"
            className={`ex_btn ${selected ? "ex_btn--remove" : "ex_btn--add"}`}
            onClick={onToggle}
            aria-pressed={selected}
            aria-label={`${selected ? "Remove" : "Add"} ${feature.title}`}
          >
            {selected ? "Remove" : "Add"}
          </button>
        )}
      </div>
    </article>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function ExplorePageUAGridLayout() {
  const navigate = useNavigate();
  const params = useParams();
  const userId = React.useMemo(() => {
    if (params.userid) return params.userid;
    try {
      return localStorage.getItem("userId") || "default";
    } catch {
      return "default";
    }
  }, [params.userid]);

  const [selected, setSelected] = React.useState(loadSelected);
  const [likeCounts, setLikeCounts] = React.useState(() =>
    Object.fromEntries(KLEIDO_FEATURES.map((f) => [f.code, loadLikeCount(f.code)]))
  );
  const [liked, setLiked] = React.useState(() =>
    Object.fromEntries(KLEIDO_FEATURES.map((f) => [f.code, readJson(STORAGE.liked(f.code), false) === true]))
  );
  const [reviews, setReviews] = React.useState(() =>
    Object.fromEntries(KLEIDO_FEATURES.map((f) => [f.code, loadReviews(f.code)]))
  );
  const [reviewFor, setReviewFor] = React.useState(null);
  const [saveError, setSaveError] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  const optional = KLEIDO_FEATURES.filter((f) => !f.locked);
  const allSelected = optional.every((f) => selected.includes(f.code));
  const totalPrice = selected.reduce((sum, code) => sum + (KLEIDO_FEATURES.find((f) => f.code === code)?.price ?? 0), 0);

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const toggleFeature = (code) => {
    if (LOCKED_CODES.includes(code)) return;
    setSaveError("");
    setSelected((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));
  };

  const handleToggleAll = () => {
    setSaveError("");
    setSelected(allSelected ? [...LOCKED_CODES] : KLEIDO_FEATURES.map((f) => f.code));
  };

  // One like per user per feature (click again to undo).
  const handleLike = (code) => {
    const nowLiked = !liked[code];
    const next = Math.max(0, (likeCounts[code] ?? 0) + (nowLiked ? 1 : -1));
    setLiked((p) => ({ ...p, [code]: nowLiked }));
    setLikeCounts((p) => ({ ...p, [code]: next }));
    writeJson(STORAGE.liked(code), nowLiked);
    writeJson(STORAGE.likes(code), next);
  };

  const handleSubmitReview = (code, text) => {
    const updated = [text, ...(reviews[code] || [])];
    setReviews((p) => ({ ...p, [code]: updated }));
    writeJson(STORAGE.reviews(code), updated);
  };

  const handleResetStats = (code) => {
    if (!window.confirm("Reset likes and reviews for this feature? This cannot be undone.")) return;
    const seed = SEED_LIKES[code] ?? 0;
    writeJson(STORAGE.likes(code), seed);
    writeJson(STORAGE.liked(code), false);
    writeJson(STORAGE.reviews(code), []);
    setLikeCounts((p) => ({ ...p, [code]: seed }));
    setLiked((p) => ({ ...p, [code]: false }));
    setReviews((p) => ({ ...p, [code]: [] }));
  };

  /** Saves the selection + what was added / removed since the last save, then opens the feed. */
  const handleSave = async () => {
    const stored = loadSelected(); // last saved selection
    const current = [...new Set([...LOCKED_CODES, ...selected])];
    const addFeatures = current.filter((c) => !stored.includes(c));
    const removeFeatures = stored.filter((c) => !current.includes(c));

    setSaving(true);
    setSaveError("");
    try {
      const ok = await saveSelectedFeatures(current);
      if (ok === false) throw new Error("Failed to save your features. Please try again.");
      writeJson(STORAGE.selected, current);
      writeJson(STORAGE.added, addFeatures);
      writeJson(STORAGE.removed, removeFeatures);
      navigate(`/user-feed/${userId}`);
    } catch (err) {
      setSaveError((err && err.message) || "Failed to save your features. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => navigate(`/user-feed/${userId}`); // unsaved changes are discarded
  const handleGoToSettings = () => navigate(`/settings/${userId}`);

  const reviewFeature = reviewFor ? KLEIDO_FEATURES.find((f) => f.code === reviewFor) : null;

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="ex_page">
      <main className="ex_main">
        {/* Header */}
        <header className="ex_header">
          <div className="ex_header-left">
            <img src={kleidoLogo} alt="Kleido" className="ex_logo" />
            <h1 className="ex_header-title">Explore Features</h1>
          </div>
          <button type="button" className="ex_settings-btn" onClick={handleGoToSettings} title="Go to Settings">
            <span aria-hidden="true">⚙️</span>
            <span className="ex_settings-label">Settings</span>
          </button>
        </header>

        {/* Selection summary */}
        <div className="ex_summary" aria-live="polite">
          <span>
            {selected.length} of {KLEIDO_FEATURES.length} features selected
          </span>
          <span className="ex_summary-total">
            ${totalPrice.toFixed(2)}
            <span>/mo</span>
          </span>
        </div>

        {/* Feature grid */}
        <section className="ex_grid" aria-label="Kleido features">
          {KLEIDO_FEATURES.map((f) => (
            <FeatureCard
              key={f.code}
              feature={f}
              selected={f.locked || selected.includes(f.code)}
              likes={likeCounts[f.code] ?? 0}
              liked={!!liked[f.code]}
              reviewCount={(reviews[f.code] || []).length}
              onToggle={() => toggleFeature(f.code)}
              onLike={() => handleLike(f.code)}
              onOpenReviews={() => setReviewFor(f.code)}
            />
          ))}
        </section>

        {/* Add all · Cancel · Save */}
        <div className="ex_actions">
          <button type="button" className="ex_btn ex_btn--remove ex_btn--wide" onClick={handleToggleAll}>
            {allSelected ? "Remove all features" : "Add all features"}
          </button>
          <div className="ex_actions-right">
            <button type="button" className="ex_btn ex_btn--remove ex_btn--wide" onClick={handleCancel} disabled={saving}>
              Cancel
            </button>
            <button type="button" className="ex_btn ex_btn--add ex_btn--wide" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
        {saveError && (
          <p className="ex_error" role="alert">
            {saveError}
          </p>
        )}
      </main>

      {/* Initiumnet signage */}
      <footer className="ex_footer">
        <p className="ex_signage">
          <span className="ex_signage-by">Powered by</span>
          <span className="ex_signage-name">INITIUMNET</span>
        </p>
        <p className="ex_copyright">© {new Date().getFullYear()} Initiumnet Technologies Pvt Ltd. All rights reserved.</p>
      </footer>

      {reviewFeature && (
        <ReviewsDialog
          feature={reviewFeature}
          reviews={reviews[reviewFeature.code] || []}
          onSubmit={(text) => handleSubmitReview(reviewFeature.code, text)}
          onReset={() => handleResetStats(reviewFeature.code)}
          onClose={() => setReviewFor(null)}
        />
      )}
    </div>
  );
}